// src/services/classReactions.js
// Firestore helpers for class reactions (emoji reactions on live + on-demand)
// Collection: classReactions
// Doc ID: ${userId}_${targetId}_${emoji}
// Schema: { userId, targetId, targetType, emoji, createdAt }
// Aggregate reactions stored on liveClasses / classRecordings docs as reactions: { heart, fire, strong, clap }

import { db, firebaseEnabled } from './firebase';
import { doc, getDoc, collection, query, where, getDocs, increment, onSnapshot, orderBy, limit, serverTimestamp } from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import { setDoc, deleteDoc, updateDoc, addDoc } from './firestoreWrite';

export const REACTION_EMOJIS = [
    { key: 'heart', emoji: '\u2764\uFE0F', label: 'Love' },
    { key: 'fire', emoji: '\uD83D\uDD25', label: 'Fire' },
    { key: 'strong', emoji: '\uD83D\uDCAA', label: 'Strong' },
    { key: 'clap', emoji: '\uD83D\uDC4F', label: 'Clap' },
];

// ── Toggle a reaction (add or remove) — returns true if added, false if removed ──
export async function toggleReaction(userId, targetId, targetType, emoji) {
    if (!firebaseEnabled || !db || !userId || !targetId || !emoji) return false;
    try {
        const docId = `${userId}_${targetId}_${emoji}`;
        const ref = doc(db, 'classReactions', docId);
        const existing = await getDoc(ref);

        // Determine which collection holds the parent doc
        const parentCollection = targetType === 'recording' ? 'classRecordings' : 'liveClasses';
        const reactionField = `reactions.${emoji}`;

        if (existing.exists()) {
            await deleteDoc(ref);
            try { await updateDoc(doc(db, parentCollection, targetId), { [reactionField]: increment(-1) }); } catch (e) { /* parent may not exist */ }
            return false;
        } else {
            await setDoc(ref, { userId, targetId, targetType, emoji, createdAt: new Date().toISOString() });
            try { await updateDoc(doc(db, parentCollection, targetId), { [reactionField]: increment(1) }); } catch (e) { /* parent may not exist */ }
            return true;
        }
    } catch (e) {
        console.error('Error toggling reaction:', e);
        return false;
    }
}

// ── Get all emojis the user has reacted with on a specific target ──
export async function getUserReactions(userId, targetId) {
    if (!firebaseEnabled || !db || !userId || !targetId) return new Set();
    try {
        const q = query(
            collection(db, 'classReactions'),
            where('userId', '==', userId),
            where('targetId', '==', targetId)
        );
        const snap = await getDocs(q);
        return new Set(snap.docs.map((d) => d.data().emoji));
    } catch (e) {
        console.error('Error fetching user reactions:', e);
        return new Set();
    }
}

// ═══════════════════════════════════════════════════════════════
//  LIVE REACTION TAPS — ephemeral fan-out for shared-moment spectacle
// ═══════════════════════════════════════════════════════════════
// During a live class (true-live or pre-recorded), each tap writes a lightweight
// event to liveClasses/{classId}/reactionEvents. All viewers + admin subscribe
// and render a floating emoji when a new event arrives. Events are ephemeral —
// we only care about the latest 20 and skip the initial snapshot so subscribers
// don't replay history on join.

// ── Send a live reaction tap (fire-and-forget) ──
export async function sendReactionTap(classId, userId, emoji) {
    if (!firebaseEnabled || !db || !classId || !userId || !emoji) return;
    try {
        await addDoc(collection(db, 'liveClasses', classId, 'reactionEvents'), {
            emoji,
            userId,
            createdAt: serverTimestamp(),
        });
    } catch (e) {
        console.error('Error sending reaction tap:', e);
    }
}

// ── Subscribe to live reaction taps ──
// Calls onNewTap({id, emoji, userId}) for each NEW tap after subscribe time.
// Rather than blindly skipping the first callback (which would miss a user's
// own first tap if they tapped before the initial snapshot arrived), we record
// the subscribe time and skip events whose `createdAt` resolves to before then.
// Pending local writes (createdAt === null until server confirms) are treated
// as fresh — so self-taps always spawn an emoji. Filters by
// docChanges().type === 'added' so server confirmation of a local write
// (which fires as 'modified' when serverTimestamp resolves) doesn't double-spawn.
// Returns an unsubscribe function.
export function subscribeToReactionTaps(classId, onNewTap) {
    if (!firebaseEnabled || !db || !classId || typeof onNewTap !== 'function') return () => {};
    const subscribeTime = Date.now();
    const q = query(
        collection(db, 'liveClasses', classId, 'reactionEvents'),
        orderBy('createdAt', 'desc'),
        limit(20)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
        snap.docChanges().forEach((change) => {
            if (change.type !== 'added') return;
            const data = change.doc.data();
            if (!data.emoji) return;
            // Historical events (created before we subscribed) — skip.
            // Pending local writes have createdAt === null; treat as fresh.
            const createdAtMs = data.createdAt?.toMillis?.();
            if (createdAtMs && createdAtMs < subscribeTime) return;
            onNewTap({
                id: change.doc.id,
                emoji: data.emoji,
                userId: data.userId,
            });
        });
    }, (err) => {
        console.error('Error subscribing to reaction taps:', err);
    });
    return unsubscribe;
}

// ── Batch save reactions (for live classes — write all at once on unmount) ──
// reactions: { heart: true, fire: true, ... } — only keys that are true get written
export async function batchReactions(userId, targetId, targetType, reactions) {
    if (!firebaseEnabled || !db || !userId || !targetId) return;
    try {
        const parentCollection = targetType === 'recording' ? 'classRecordings' : 'liveClasses';
        for (const [emoji, active] of Object.entries(reactions)) {
            const docId = `${userId}_${targetId}_${emoji}`;
            const ref = doc(db, 'classReactions', docId);
            const existing = await getDoc(ref);

            if (active && !existing.exists()) {
                await setDoc(ref, { userId, targetId, targetType, emoji, createdAt: new Date().toISOString() });
                try { await updateDoc(doc(db, parentCollection, targetId), { [`reactions.${emoji}`]: increment(1) }); } catch (e) { /* non-critical */ }
            } else if (!active && existing.exists()) {
                await deleteDoc(ref);
                try { await updateDoc(doc(db, parentCollection, targetId), { [`reactions.${emoji}`]: increment(-1) }); } catch (e) { /* non-critical */ }
            }
        }
    } catch (e) {
        console.error('Error batch saving reactions:', e);
    }
}
